import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { Tooltip } from '@mui/material';
import Logo from 'assets/images/hjlogo.png';
import { TABLE_TYPE_SITNGO, TABLE_TYPE_TOURNAMENT } from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import GlobalContext from 'contexts/GlobalContext';
import { useGridMode } from 'contexts/GridModeContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import { MAX_OPEN_TABLES, ViewManagerContext } from 'contexts/ViewManagerContext';
import gsap from 'gsap';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useChromeInset } from 'hooks/useChromeInset';
import { useExitToNextTable } from 'hooks/useExitToNextTable';
import { useGuest } from 'hooks/useGuest';
import { useIsNarrowTableView } from 'hooks/useIsNarrowTableView';
import { useOrientation } from 'hooks/useOrientation';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { useTableSelector } from 'hooks/useTableSelector';
import { useViewportWidth } from 'hooks/useViewportWidth';
import { TableType, updateTableType } from 'reducers/lobby';
import { acceptSeatOffer } from 'reducers/waitlist';
import useGameService from 'services/GameService';
import { saloonIconBtnStyle } from 'styles/saloonButtons';
import { saloonFonts, saloonTokens } from 'styles/saloonTheme';
import type { WaitlistEntry } from 'types/waitlist';
import { consumeSupportIntent, markSupportIntentBeforeLogin } from 'utils/auth';
import { currencyFormatter } from 'utils/functions';
import { GUEST_CTA } from 'utils/guestCta';
import { addTableSlotsInUse } from 'utils/tableCapacity';
import { clampTablesInView, tableCountOptions } from 'utils/tableViewWidth';
import { APP_DOMAIN } from 'utils/urls';

import { CashierContext } from 'components/Cashier/CashierContext';
import { GameMode } from 'components/LobbyTable/LobbyTable';
import GuestCtaButton from 'components/LoginPrompt/GuestCtaButton';
import { promptLogin } from 'components/LoginPrompt/loginPromptStore';
import NotificationCenter from 'components/Notifications/NotificationCenter';
import AccountMenuPanel from 'components/Profile/AccountMenuPanel';
import { ActiveTable } from 'components/SaloonLobby/types';
import { useActiveTables } from 'components/SaloonLobby/useActiveTables';
import YourTables from 'components/SaloonLobby/YourTables';
import { TILE_GAP } from 'components/SaloonLobby/YourTables/YourTables';
import AvatarFrame from 'components/Streaks/AvatarFrame';
import { useOwnStreakFrame } from 'components/Streaks/state/useSeatStreakFrame';
import { useSupportContext } from 'components/Support/SupportContext';
import PokerChip from 'components/Table/PokerChip';
import TableShutdownBanner, {
  TABLE_SHUTDOWN_MESSAGE
} from 'components/TableShutdownBanner/TableShutdownBanner';
import TickerBanner from 'components/TickerBanner/TickerBanner';
import { getTickerVisible } from 'components/TickerBanner/tickerVisibility';
import TableActions from 'components/UserActions/TableActions';
import WaitlistChip from 'components/Waitlist/WaitlistChip';

import {
  clusterReachWithin,
  lastClusterReach,
  rememberClusterReach,
  stripBandInsets
} from './stripBandInsets';
import { DOCK_BAND_MAX_WIDTH } from './tableClusterMetrics';
import TableCountPicker from './TableCountPicker';
import TableMenuPanel from './TableMenuPanel';
import TableSwitcher from './TableSwitcher';
import { applyTickerShift } from './tickerShift';
import { DiamondIcon, PokerChipIcon, SheriffStarIcon, SpadeIcon } from './WesternIcons';

type TableHeaderProps = {
  hideFull?: boolean;
  onToggleHideFull?: () => void;
  activeGameModes?: Set<string>;
  onToggleGameMode?: (mode: string) => void;
  activeTournamentTypes?: Set<string>;
  onToggleTournamentType?: (key: string) => void;
  onOpenFilters?: () => void;
  compact?: boolean; // true = just branding + nav + balance + profile (game page)
  // Landscape single-table view only: render the header as transparent floating
  // overlays on the felt (no bar) instead of the solid compact row. The grid view
  // (MultiTableGrid) keeps the bar, so this is opt-in from Game.tsx.
  tableOverlay?: boolean;
  // Reports whether the in-table scrolling ticker is actually on screen, so a
  // sibling (Game.tsx's landscape action rail) can shift down to clear it the
  // same way this header's own floating clusters do (withTickerShift).
  onTickerVisibleChange?: (visible: boolean) => void;
  /**
   * The panel this header instance lives inside, when it belongs to exactly
   * one. A table↔grid switch is a double-buffered cut (SlideContainer): the
   * LEAVING panel stays painted on top for two frames after `activeView` has
   * already flipped. A header that renders from the global `activeView` morphs
   * during that window — the grid's header briefly swapped its dock for the
   * legacy Lobby-button + gameId-chip row, which is the "old style header"
   * flash frame isolated on the 2→1 switch (Jason 8/21). Presentation
   * therefore renders from THIS value; the global `activeView` is only for
   * behaviour that must know where the player really is (count-picker
   * routing). Unset = follow `activeView` (stories, demo surfaces).
   */
  panelView?: 'table' | 'grid';
};

const tournamentTypeOptions: { key: string; label: string }[] = [
  { key: 'nlh', label: 'NLH' },
  { key: 'bounty', label: 'Bounty' },
  { key: 'freezeout', label: 'Freezeout' },
  { key: 'freeroll', label: 'Freeroll' },
  { key: 'satellite', label: 'Satellite' }
];

// Per-game nav chip — own its own animation lifecycle so the turn-glow
// tween is created once and killed on unmount/state change. The previous
// inline ref-callback created a new repeat:-1 tween on every parent render
// without killing the old ones, leaking tweens indefinitely.
const NavChipButton = ({
  game,
  isActive,
  gameLabel,
  onClick
}: {
  game: ActiveTable;
  isActive: boolean;
  gameLabel: string;
  onClick: () => void;
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const showTurnGlow = game.isMyTurn && !isActive;

  useEffect(() => {
    const el = btnRef.current;
    if (!el || !showTurnGlow) return;
    // Static shadow + composited opacity pulse — same pattern as the
    // table seat glow. Avoids per-frame repaint of box-shadow.
    gsap.set(el, { boxShadow: '0 0 10px rgba(233, 140, 38, 0.4)' });
    const tween = gsap.fromTo(
      el,
      { opacity: 0.78 },
      // Finite: repeat:-1 keeps GSAP's ticker (and the frame pipeline) hot for
      // as long as the chip is shown. 9 plays of pulsing, ending parked at the
      // bright state (yoyo + even repeat ends on the forward pass). See
      // docs/investigations/20260819-game-client-overheating.md (8c/8d).
      { opacity: 1, duration: 0.8, repeat: 8, yoyo: true, ease: 'sine.inOut' }
    );
    return () => {
      tween.kill();
      gsap.set(el, { boxShadow: '', opacity: 1 });
    };
  }, [showTurnGlow]);

  return (
    <button
      ref={btnRef}
      onClick={onClick}
      style={{
        ...navChipStyle,
        ...(isActive ? navChipActiveStyle : {}),
        ...(showTurnGlow ? navChipTurnStyle : {})
      }}>
      <PokerChip size={12} color={game.game === 'omaha' ? 'gold' : 'red'} />
      <span>#{game.gameID}</span>
      <span style={navChipTypeStyle}>{gameLabel}</span>
      {game.isMyTurn && <span style={turnDotStyle} />}
    </button>
  );
};

const TableHeader = ({
  hideFull = false,
  onToggleHideFull,
  activeGameModes,
  onToggleGameMode,
  activeTournamentTypes,
  onToggleTournamentType,
  onOpenFilters,
  compact = false,
  tableOverlay = false,
  onTickerVisibleChange,
  panelView
}: TableHeaderProps) => {
  const dispatch = useAppDispatch();
  // Publishes the "Your Tables" strip's height so the felt reserves it instead
  // of having its top seats painted over — see hooks/useChromeInset.
  const attachChromeInset = useChromeInset();
  const {
    activeView,
    activeTableInfo,
    returnToLobby,
    openTable,
    openGrid,
    setMultiTableEnabled,
    tablesInView,
    setTablesInView,
    selectedGridGameID,
    selectGridTable
  } = useContext(ViewManagerContext);
  // What this header LOOKS like. Pinned instances (the grid's header, Game's
  // overlay headers) never follow a mid-cut `activeView` flip — see the
  // panelView prop comment. Click handlers below still read `activeView`
  // directly: routing decisions need the real current view, not the paint.
  const headerView = panelView ?? activeView;
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const supportsHover = useSupportsHover();
  // The multi-table grid ("All Tables") is a desktop-browser-only feature
  // (mirrors Unity, where the multitable toggle exists only in the WebGL
  // build). On mobile — native app or mobile web, portrait or landscape —
  // players stay on a single active table and switch via the table fan or
  // swipe navigation, so the grid entry points are hidden.
  const isMobile = !supportsHover;
  // WIDTH decides the table-switching experience, pointer type does not
  // (Jason 2026-08-15): a desktop window narrowed to one table's width is the
  // same situation as a phone, so it gets the same navigation — the positional
  // strip with one fan per open table (active highlighted) and swipe/fan
  // switching. Before this, a narrow DESKTOP window kept the spread-fan header
  // (current table filtered out), which read as a mystery toggle with no
  // indicator — two different experiences for the same width.
  const isNarrowViewport = useIsNarrowTableView();
  const useStripNav = isMobile || isNarrowViewport;
  // True when this header is sitting above TILED felts — several tables sharing
  // the viewport, each with its own controls.
  const tiled = useGridMode();
  // Live viewport width — the table-count control's options depend on it, and
  // it has to re-offer them the moment the window is dragged wider or narrower
  // rather than at the next unrelated re-render.
  const viewportWidth = useViewportWidth();
  // The navigation strip shares the top of the felt with the floating corner
  // clusters, and its cluster/"+" sit at the band's edges — so the band has to
  // stop short of them. Their widths are content-driven (button labels; Add
  // Chips mounts/unmounts with top-up eligibility), so measure rather than
  // guess: a fixed inset let the newest card slide under Sit Out / Observe.
  // Callback refs (elements in state) so the measuring effect re-runs when the
  // clusters actually mount/swap — not on every game-tick re-render.
  const [overlayRootEl, setOverlayRootEl] = useState<HTMLDivElement | null>(null);
  const [leftClusterEl, setLeftClusterEl] = useState<HTMLDivElement | null>(null);
  const [rightClusterEl, setRightClusterEl] = useState<HTMLDivElement | null>(null);
  // Seeded from the last measurement rather than zero: a table switch remounts
  // this header, and starting from "nothing measured" paints one frame at the
  // fallback insets before snapping to the real ones — the strip visibly
  // zooming on every swipe.
  const [clusterReach, setClusterReach] = useState(lastClusterReach);
  useLayoutEffect(() => {
    const measure = () => {
      // REACH, not width: how far each cluster extends inward from its side of
      // the header — which folds in the cluster's own edge offset, safe-area
      // inset and any scale transform. Measured INSIDE the overlay root rather
      // than against the viewport, so a header sitting in an off-screen
      // carousel panel measures the same as one on screen.
      const { left, right } = clusterReachWithin(
        overlayRootEl?.getBoundingClientRect(),
        leftClusterEl?.getBoundingClientRect(),
        rightClusterEl?.getBoundingClientRect()
      );
      // A fresh mount measures 0 until its refs are attached; don't let that
      // wipe the remembered value and re-introduce the zoom.
      if (left <= 0 && right <= 0) return;
      rememberClusterReach({ left, right });
      setClusterReach((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right }
      );
    };
    measure();
    // The right cluster grows/shrinks as its buttons come and go mid-hand (Add
    // Chips, Sit Out / Observe), so observe it rather than only re-measuring on
    // resize/rotation.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    if (observer) {
      if (overlayRootEl) observer.observe(overlayRootEl);
      if (leftClusterEl) observer.observe(leftClusterEl);
      if (rightClusterEl) observer.observe(rightClusterEl);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [overlayRootEl, leftClusterEl, rightClusterEl]);
  // Desktop-only transform hover; touch keeps the first tap (iOS WKWebView).
  const saloonHover = supportsHover
    ? {
        onMouseEnter: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1.04)';
        },
        onMouseLeave: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        }
      }
    : {};
  // Mobile-landscape (e.g. iPhone Pro Max ≈ 932×430) reports as 'landscape'
  // because width > height, but the actual room for the filter row is still
  // tight — everything-on-one-line layout overflows and the Filters button
  // wraps to its own line. Treat anything under ~1024px as a "narrow" viewport
  // and use the same compact two-row layout we use on portrait phones.
  const [isNarrow, setIsNarrow] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  );
  useEffect(() => {
    const handleResize = () => setIsNarrow(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const useCompactFilterLayout = isPortrait || isNarrow;
  const tableType = useAppSelector((state) => state.lobby.tableTypeSelected);
  const userBalance = useAppSelector((state) => state.user.playerBankAmount);
  const userChargeBalance = useAppSelector((state) => state.user.playerChargeBalance);
  const formattedPromoBalance = currencyFormatter(userChargeBalance);
  const playerAvatar = useAppSelector((state) => state.user.playerAvatar);
  // The streak frame around the profile button, as in the lobby header (EN-11164).
  const streakFrame = useOwnStreakFrame();
  // Account menu / profile is unavailable during maintenance (Unity parity).
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule?.isMaintenance);
  // Whether any scrolling-notification campaign targets the in-table context.
  // The single-table floating-overlay branches (no header bar) use this to
  // float a ticker at the very top and push their corner button groups below
  // it; grid + lobby render the ticker via TopScrollingNotificationBanner /
  // SaloonHeader instead. Mirrors the filter in TopScrollingNotificationBanner.
  const tableCampaigns = useAppSelector((state) => (state as any).notifications?.campaigns || []);
  const showTableTicker = (tableCampaigns as any[]).some(
    (c: any) =>
      !c.rotation_paused &&
      c.messages?.length > 0 &&
      (!c.contexts || c.contexts.includes('table') || c.contexts.includes('all'))
  );

  // Slide the top-anchored floating clusters down (animated) so the in-table
  // ticker gets its own row instead of overlapping the header buttons. Driven by
  // the ticker's ACTUAL visibility (reported by TickerBanner) rather than merely
  // "a campaign exists" — the banner self-hides when paused/dismissed/between
  // messages, and the buttons must slide back up when it does.
  // SEEDED with the banked visibility: this header remounts on every table
  // switch, and starting at false while the ticker was showing replayed the
  // 0.3s cluster slide (the styles carry a `top` transition, and the marquee
  // effect forces a style resolution before the corrected state lands) - the
  // header elements visibly re-animating on every swipe.
  const [tickerVisible, setTickerVisible] = useState(() => getTickerVisible('table'));
  // Update local state AND notify the parent (Game.tsx) so its landscape action
  // rail can shift down to clear the ticker in step with this header's clusters.
  const handleTickerVisibleChange = useCallback(
    (visible: boolean) => {
      setTickerVisible(visible);
      onTickerVisibleChange?.(visible);
    },
    [onTickerVisibleChange]
  );
  const withTickerShift = (base: CSSProperties): CSSProperties =>
    applyTickerShift(base, tickerVisible);
  const { openCashier, closeCashier } = useContext(CashierContext);
  const { setOpenAddChips } = useContext(GameContext);
  const { setCloseBuyInModal } = useContext(GlobalContext);
  // Table top-up availability for the current single-table view — mirrors the
  // gating that used to live on the bottom-bar "+" (TopAction). The Saloon
  // header "Add Chips" now performs the seat top-up rather than opening the
  // cashier (the balance pill already does that), so it's only shown when a
  // top-up is actually possible.
  const isPlayerSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const gameTableType = useTableSelector((table) => table.game.tabletype);
  const tableClosed = useTableSelector((table) => table.game?.closed);
  const tableClosingAfterHand = useTableSelector((table) => table.game?.closing_after_hand);
  const tableGameID = useTableSelector((table) => table.game?.gameID);

  const exitToNextTable = useExitToNextTable();
  const { leaveTable } = useGameService();

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let visibilityHandler: (() => void) | undefined;

    if (Number(tableClosed) === 1 && gameTableType === 's' && tableGameID) {
      leaveTable();
      const exit = () => exitToNextTable(tableGameID);
      if (document.visibilityState !== 'visible') {
        // Tab is backgrounded — defer until the user returns to avoid the timer
        // being stalled by browser background throttling and leaving the player
        // stuck at the table until they foreground the tab.
        visibilityHandler = () => {
          if (document.visibilityState === 'visible') {
            const currentVisibilityHandler = visibilityHandler;
            if (currentVisibilityHandler) {
              document.removeEventListener('visibilitychange', currentVisibilityHandler);
            }
            visibilityHandler = undefined;
            exit();
          }
        };
        document.addEventListener('visibilitychange', visibilityHandler);
      } else {
        timeoutId = setTimeout(exit, 1000);
      }
    }

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
    };
  }, [exitToNextTable, leaveTable, tableClosed, gameTableType, tableGameID]);
  const maximumTopUp = useTableSelector((table) => table.game.maximumTopUp);
  const canTopUp =
    isPlayerSeated &&
    userBalance >= 1 &&
    gameTableType !== TABLE_TYPE_TOURNAMENT &&
    gameTableType !== TABLE_TYPE_SITNGO &&
    maximumTopUp > 0;
  // Top-up (Add Chips) only ever applies to cash tables. On tournaments/SNGs the
  // Add Chips slot in the top-right stack must NOT be reserved — reserving it
  // (visibility:hidden placeholder) leaves dead space that pushes Exit Table
  // down. Cash keeps the reserved slot because canTopUp legitimately toggles
  // mid-hand and we don't want the controls to jump.
  const isCashTable =
    gameTableType !== TABLE_TYPE_TOURNAMENT && gameTableType !== TABLE_TYPE_SITNGO;
  const {
    openPanel: openSupport,
    closePanel: closeSupport,
    unreadCount: supportUnread
  } = useSupportContext();
  // Guests can't use member support — tapping support shows a login advisory
  // instead of opening the panel (see handleOpenSupport). `guest` also gates the
  // member money/account chrome in the compact tableOverlay branches below.
  const guest = useGuest();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  // Hamburger → in-table menu panel (Go back to Lobby / Cashier / Table Settings
  // / Table History / Exit Table). Distinct from the lobby AccountMenuPanel.
  const [showTableMenu, setShowTableMenu] = useState(false);
  // Governs whether open tables collapse into the "N TABLES" switcher badge
  // (stacked) or spread out as individual fans across the header.
  const { stackTables } = useContext(TableSettingsContext);

  const handleOpenMenu = useCallback(() => {
    // The in-table menu (Go back to Lobby / Cashier / Table Settings / Table
    // History / Exit Table) is navigation and stays available during
    // maintenance — the marquee gates actions, it never blocks the menu itself.
    // Only the Cashier row inside is disabled during maintenance (and
    // openCashier has its own maintenance guard), so opening the panel is safe.
    setShowTableMenu((prev) => {
      if (!prev) {
        closeCashier();
        closeSupport();
      }
      return !prev;
    });
  }, [closeCashier, closeSupport]);

  // Header panels (Cashier / Support / Profile) are mutually exclusive — opening
  // one always closes the others, so users never see two overlapping fly-outs.
  const handleOpenCashier = useCallback(
    (...args: Parameters<typeof openCashier>) => {
      setShowProfileMenu(false);
      closeSupport();
      openCashier(...args);
    },
    [openCashier, closeSupport]
  );

  const handleOpenSupport = useCallback(() => {
    // Guests can't use member support — advise them to log in instead of
    // opening the support panel.
    if (guest) {
      markSupportIntentBeforeLogin();
      promptLogin({
        title: 'Log in to contact support',
        message:
          "You're browsing as a guest. Log in or create an account to contact our support team.",
        dismissLabel: 'Not now'
      });
      return;
    }
    setShowProfileMenu(false);
    closeCashier();
    openSupport();
  }, [guest, openSupport, closeCashier]);

  // A guest who chose to log in from the support advisory (markSupportIntentBeforeLogin
  // above) lands back on whichever page they were on, authenticated. Open the
  // support panel for them instead of making them find this button again.
  useEffect(() => {
    if (guest) return;
    if (!consumeSupportIntent()) return;
    setShowProfileMenu(false);
    closeCashier();
    openSupport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest]);

  // Seat chip top-up (formerly the bottom-bar "+"). Opens the AddChipsModal.
  // A voluntary top-up must NOT hide the player's turn clock — the engine clock
  // keeps running regardless, and turning isTimerOff on here left the seat timer
  // stuck off after the modal closed. The busted-rebuy flow (Player.tsx) still
  // owns isTimerOff where the in-modal countdown actually needs it.
  const handleOpenAddChips = useCallback(() => {
    setOpenAddChips(true);
    setCloseBuyInModal(false);
  }, [setOpenAddChips, setCloseBuyInModal]);

  const handleToggleProfile = useCallback(() => {
    // Account menu (cashier / profile / etc.) is blocked during maintenance —
    // don't open it, but always allow closing an already-open menu.
    if (isMaintenance && !showProfileMenu) return;
    setShowProfileMenu((prev) => {
      // About to open → close the other panels first.
      if (!prev) {
        closeCashier();
        closeSupport();
      }
      return !prev;
    });
  }, [closeCashier, closeSupport, isMaintenance, showProfileMenu]);

  useEffect(() => {
    const handleOpenProfilePanel = (event: Event) => {
      if (isMaintenance) return; // profile blocked during maintenance
      // EN-11383: only the header of the panel on screen answers. The grid's
      // header stays mounted under the lobby (and under a single table), so
      // without this the lobby's streak banner opened this AccountMenuPanel
      // too, stacked under SaloonHeader's: a backdrop click closed one and the
      // other looked like the drawer opening a second time.
      if (activeView === 'lobby' || (panelView && panelView !== activeView)) return;
      const customEvent = event as CustomEvent<{ field?: string }>;
      const fieldToFocus = customEvent.detail?.field;

      closeCashier();
      closeSupport();
      setShowProfileMenu(true);

      if (fieldToFocus) {
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent('focus-profile-field', { detail: { field: fieldToFocus } })
          );
        }, 0);
      }
    };

    window.addEventListener('open-profile-panel', handleOpenProfilePanel);
    return () => window.removeEventListener('open-profile-panel', handleOpenProfilePanel);
  }, [closeCashier, closeSupport, isMaintenance, activeView, panelView]);

  const currentGameID = activeTableInfo?.gameID;

  // 'omahaHilo' isn't part of the legacy GameMode enum — keep it as a distinct string key
  // so the parent can map it to the Omaha Hi/Lo filter independently of plain Omaha.
  const gameModes = [
    { key: GameMode.texas, label: "Hold 'Em", icon: <SpadeIcon size={13} /> },
    { key: GameMode.omaha, label: 'Omaha', icon: <DiamondIcon size={13} /> },
    { key: 'omahaHilo', label: 'Hi/Lo', icon: <DiamondIcon size={13} /> }
  ];

  const tableTypes = [
    { key: TableType.CashGames, label: 'Cash', icon: <PokerChipIcon size={18} /> },
    {
      key: TableType.SitNGo,
      label: 'Sit & Go',
      icon: <TimerOutlinedIcon style={{ fontSize: '1.05rem' }} />
    },
    { key: TableType.Standard, label: 'Tournaments', icon: <SheriffStarIcon size={18} /> }
  ];

  // Shared "Your Tables" derivation — same hook the lobby uses, so the header's
  // YourTables fan shows identical hole cards / seated-observing-folded state /
  // left→right order as the lobby. openTableOrdered records the click-time slot
  // in the persisted order (the plain openTable from ViewManagerContext does not).
  // activeGames: the open tables (cap, counts, grid). navGames: what the nav
  // shows — the same order plus queued tables not loaded yet, in their slots.
  const {
    activeTables: activeGames,
    navTables: navGames,
    slotsInUse,
    heldSlots,
    openTable: openTableOrdered
  } = useActiveTables();
  // The "+" opens another table, so it goes once every slot is in use — a
  // rebalance in flight included (see slotsInUse) — counted as the cap counts
  // them, waitlist cards included (heldSlots), so it never offers a join the
  // cap would refuse (EN-11131).
  const addTable =
    addTableSlotsInUse(slotsInUse, heldSlots) >= MAX_OPEN_TABLES ? undefined : returnToLobby;
  // The strip and the stacked switcher live on the SINGLE-TABLE view, where a
  // tap means "put this table in front of me" — never "change which view I am
  // in". Without forceSingle, ViewManager's sticky grid preference (flipped by
  // the add-a-table auto-grow) routed an already-open table to /grid: tapping
  // a dock card right after adding a second table yanked the player out of
  // single view and into the two-up grid (owner repro, 8/25 — same rule as
  // gestures: a table switch must never re-route the view). The grid's own
  // dock (below) is untouched: it focuses cells via selectGridTable.
  const switchTableInPlace = useCallback(
    (gameID: string, game: string) => openTableOrdered(gameID, game, 'no', { forceSingle: true }),
    [openTableOrdered]
  );
  // The stored choice is what the player ASKED for; this is what their window
  // and open-table count actually allow. Everything lays out from this one.
  // Counted by slot, so a rebalance in flight (one table between its source
  // and destination) doesn't drop the count and reshape the selector.
  const effectiveTablesInView = clampTablesInView(tablesInView, viewportWidth, slotsInUse);
  /**
   * What the player is ACTUALLY looking at — which is what the control's icon
   * must show.
   *
   * `effectiveTablesInView` is the stored PREFERENCE clamped to what would fit.
   * Showing that in single-table view made the trigger display the two-table
   * icon while exactly one felt was on screen (Jason 2026-08-17) — the control
   * reporting a layout the player is not in. Multi-table layouts only ever
   * render on the grid, so anywhere else the honest answer is one.
   */
  const tablesOnScreen = headerView === 'grid' ? effectiveTablesInView : 1;
  // The count control appears only when all three are true: the window fits
  // more than one table, the player HAS more than one open, and this is a
  // desktop browser.
  //
  // That last one is not the width rule leaking back in — it is EN-9850, which
  // makes the multi-table row desktop-only (mirroring Unity, where the toggle
  // exists only in the WebGL build). MultiTableGrid returns null on mobile, so
  // a control offered on an iPad in landscape — wide enough for two felts —
  // would hand the player a blank screen. Offering a layout the renderer
  // refuses is worse than not offering it.
  const showCountControl = !isMobile && tableCountOptions(viewportWidth, slotsInUse).length > 1;

  // Tables the player is queued on but NOT currently open — surfaced in-game as a
  // nav chip (WAIT · N / JOIN {secs}) so a waiting player keeps sight of the queue
  // and can claim a freed seat without a disruptive modal. A table the player is
  // ALSO observing already shows its fan (with the WAIT/JOIN pill) + its #gameID
  // chip, so it's excluded here to avoid a duplicate indicator.
  // Queued tables that aren't open render as waitlist chips, in their own slot
  // of the nav order (navGames) like any other table.
  const waitlistSpots = useAppSelector((state) => state.waitlist?.mySpotByGameId) || {};
  const openGameIds = new Set(activeGames.map((t) => String(t.gameID)));
  // The grid header's tiles: an open table has a cell, so it is focused in
  // place. A queued table not loaded yet has no cell — selecting it would
  // select nothing, and a JOIN card would flag the seat with no open — so it
  // is opened like anywhere else, and joins the grid. Returns whether the
  // open was accepted, which the JOIN claim gates on.
  const openFromGridHeader = (gameID: string, game: string): boolean => {
    if (openGameIds.has(String(gameID))) {
      selectGridTable(String(gameID));
      return true;
    }
    return openTableOrdered(String(gameID), game);
  };
  // Claim a ready seat — same effect the retired seat-offer modal's Accept had:
  // navigate to the offered game and flag it so its table view opens the
  // buy-in. Only once the open was accepted: a flag left by a rejected open
  // (debounce, maintenance, table cap) would open the buy-in whenever that
  // table was next opened.
  const claimReadySeat = (entry: WaitlistEntry) => {
    if (openTableOrdered(String(entry.gameId), entry.gameType)) {
      dispatch(acceptSeatOffer(entry.gameId));
    }
  };

  // Open-table previews for the header center: collapsed into the "N TABLES"
  // switcher badge when stackTables is on, or spread as individual fans when
  // off. Shared by portrait + landscape so the Stack Your Active Tables toggle
  // applies consistently across orientations.
  const renderOpenTables = (animate = false, trailing?: React.ReactNode) => {
    if (navGames.length === 0) return null;
    const activeGameID = currentGameID ? String(currentGameID) : undefined;
    // The navigation strip is a positional map of EVERY open table — the one
    // you're on included, carrying the active highlight — at every width, on
    // every device (design call, Jason 2026-08-15: two tables active means two
    // fans, desktop and phone alike; the phone and the desktop header are ONE
    // experience). This supersedes EN-10341's desktop spread, which filtered
    // the currently-viewed table out and left a lone fan that read as a
    // mystery toggle. Keep the last table's status visible too (EN-10662).
    //
    // Width only decides LAYOUT (stripLayout: the edge-pinned band with fixed
    // slot sizing at one-table widths), never CONTENTS.
    // Queued tables included, each in its own slot (see navGames).
    const stripTables = navGames;
    // Stacked: the switcher is a single badge with its own dropdown, so it has
    // no row for the selector to ride in — it trails as a sibling instead.
    return stackTables ? (
      <>
        <TableSwitcher
          tables={navGames}
          onOpenTable={switchTableInPlace}
          activeGameID={activeGameID}
          // Same "+" lifecycle as the unstacked strip: present below the cap,
          // gone once every slot is taken.
          onAddTable={addTable}
        />
        {trailing}
      </>
    ) : (
      <YourTables
        tables={stripTables}
        onOpenTable={switchTableInPlace}
        activeGameID={activeGameID}
        bare
        autoCenter={false}
        animateIn={animate}
        // Narrow: the navigation strip (see YourTables). The cap sizes its
        // slots for a full strip, so card size/spacing stay constant as tables
        // are opened, closed and switched between.
        stripLayout={useStripNav}
        stripCapacity={MAX_OPEN_TABLES}
        trailing={trailing}
        // The "+" (add table) slot — returns to the lobby to open another
        // table. Removed once the player is at the MAX_OPEN_TABLES cap, since
        // no more tables can be opened; the cap itself is enforced by
        // ViewManagerContext.openTable (client) and the connection-table limit
        // (server), which this only mirrors.
        onAddTable={addTable}
      />
    );
  };

  const isOnLobby = headerView === 'lobby';
  const showNavRow = activeGames.length > 0 || !isOnLobby;

  /**
   * The player picked how many tables they want in view.
   *
   * ONE table is the swipe carousel; TWO to FOUR is the All Tables grid, whose
   * column count honours the choice (MultiTableGrid reads the same stored
   * value). Routing between two surfaces is the interim shape: the tiled
   * one-surface version exists behind TILED_TABLE_VIEW_ENABLED but re-creates
   * containment problems the grid already solves (per-cell modals, the control
   * rail's home), so the reliable page carries multi-table until that port is
   * finished deliberately — see the flag's comment.
   *
   * Already ON the grid, a new count just changes the columns in place — no
   * navigation. And whichever way it routes, the table the player was looking
   * at stays the selected one: choosing a layout never moves their seat.
   */
  const applyTableCount = (count: number) => {
    setTablesInView(count);
    if (count === 1) {
      setMultiTableEnabled(false);
      if (activeView !== 'grid') return;
      const selected =
        activeGames.find((t) => String(t.gameID) === String(selectedGridGameID)) || activeGames[0];
      // forceSingle bypasses the still-persisted multi-table preference for
      // exactly this navigation.
      if (selected) openTable(selected.gameID, selected.game, 'no', { forceSingle: true });
      return;
    }
    setMultiTableEnabled(true);
    if (activeView === 'grid') return; // columns update in place
    if (activeView === 'table' && currentGameID) selectGridTable(String(currentGameID));
    openGrid();
  };

  // Defined once and placed in BOTH branches below: the compact header renders
  // card fans in grid view and gameId chips everywhere else, and the selector
  // trails the table area in either case. Hoisting it is what stops the two
  // placements drifting apart.
  const countSelector = showCountControl ? (
    <TableCountPicker
      openTables={slotsInUse}
      viewportWidth={viewportWidth}
      value={tablesOnScreen}
      onChange={applyTableCount}
    />
  ) : null;

  // The selector rides INSIDE the fan row as trailing content, so the row's
  // own slot geometry places it — see YourTables `trailing`. Built ONCE and
  // attached wherever the row renders: the grid header, the landscape fan row
  // AND the portrait strip band. The portrait strip was the one place that
  // didn't attach it, which stranded portrait-window players who entered
  // Multi-Table View with no control to get back (EN-10589 — the width
  // supported the grid, only this header variant was missing the control).
  const selectorTrailing = countSelector && (
    <>
      <div style={clusterDivider} />
      {countSelector}
    </>
  );

  const lobbyChip = (
    <button
      onClick={returnToLobby}
      style={{
        ...navChipStyle,
        ...(isOnLobby ? navChipActiveStyle : {})
      }}>
      <span>Lobby</span>
    </button>
  );

  // Shared nav chips JSX
  const navChips = (
    <div
      style={
        // Grid view swaps the gameId chips for card-fan previews, which need
        // vertical room for their trophy / status badges (absolutely positioned
        // outside the card box). The band cap matches the single view's dock
        // band, so YourTables' fit-to-band scale computes identically on both
        // surfaces — see DOCK_BAND_MAX_WIDTH.
        // Stacked, the switcher's dropdown renders absolutely BELOW the badge,
        // and this row's overflowX:auto (for scrolling wide fan rows) clips
        // the other axis too — the previews showed as a sliver under the
        // header. A lone badge never needs to scroll, so let it overflow, as
        // the single view's switcher row does.
        headerView === 'grid'
          ? {
              ...navContainer,
              padding: '11px 0',
              maxWidth: DOCK_BAND_MAX_WIDTH,
              ...(stackTables ? { overflowX: 'visible', overflowY: 'visible' } : null)
            }
          : navContainer
      }>
      {headerView !== 'grid' && lobbyChip}

      {headerView === 'grid' ? (
        // Multi-table view: show the card-fan previews (cards / dashed / trophy /
        // turn / status) instead of gameId chips. Tapping a fan selects that
        // table's grid cell; the selected one is highlighted.
        // Stack Your Active Tables applies here too (it is a setting for every
        // view): stacked, the fans collapse into the same switcher the single
        // view uses, and picking a table selects its cell. The count selector
        // trails it, as it trails the fans.
        navGames.length > 0 &&
        (stackTables ? (
          <>
            <TableSwitcher
              tables={navGames}
              onOpenTable={openFromGridHeader}
              activeGameID={selectedGridGameID ? String(selectedGridGameID) : undefined}
              onAddTable={addTable}
            />
            {selectorTrailing}
          </>
        ) : (
          // The multi-tabling CLUSTER: add-table (inside YourTables, leading),
          // the fan tiles, then the count selector — one unit, one gap, in that
          // order at every width. The selector used to sit back beside Lobby,
          // which read as app navigation rather than as part of the tables
          // (Jason 2026-08-17).
          <YourTables
            tables={navGames}
            onOpenTable={openFromGridHeader}
            activeGameID={selectedGridGameID ? String(selectedGridGameID) : undefined}
            // Same "+" lifecycle as the single-table strip: present below the
            // cap, gone once every slot is taken. With the Lobby button removed
            // from this header (Jason 2026-08-18), this is the grid's add-table
            // route — and the wordmark is the always-available lobby route.
            onAddTable={addTable}
            // One dot per table on the grid's screen, orange = active — replaces
            // the single pagination dot, which answers a single-table question
            // the grid doesn't ask.
            gridViewDots
            bare
            autoCenter={false}
            trailing={selectorTrailing}
          />
        ))
      ) : (
        <>
          {navGames.map((game) => {
            // A queued table that isn't open: its waitlist chip, in its slot —
            // in-game only (the lobby has the "Your Tables" strip). Lets a
            // seated player watch/claim a queued seat inline, with the
            // ready-seat countdown visible in stacked OR spread mode.
            if (!openGameIds.has(String(game.gameID))) {
              const entry = waitlistSpots[Number(game.gameID)];
              if (headerView !== 'table' || !entry) return null;
              return (
                <WaitlistChip
                  key={`wl-${game.gameID}`}
                  entry={entry}
                  onOpen={(gameId, gameType) => openTableOrdered(String(gameId), gameType)}
                  onClaim={claimReadySeat}
                />
              );
            }
            // Single-table / lobby view: compact gameId chips. The active tab
            // tracks the URL table; clicking opens it.
            const isActive = headerView === 'table' && game.gameID === currentGameID;
            const gameLabel =
              game.tabletype === 's' ? 'Cash' : game.tabletype === 't' ? 'SNG' : 'MTT';
            return (
              <NavChipButton
                key={game.gameID}
                game={game}
                isActive={isActive}
                gameLabel={gameLabel}
                onClick={() => openTable(game.gameID, game.game)}
              />
            );
          })}
          {/* Trails the table area here too, so the selector sits in the same
              place relative to the tables whichever way this header renders. */}
          {countSelector}
        </>
      )}
    </div>
  );

  // The in-game header (compact mode) uses the Saloon "tp-lh" design. Two
  // layouts share the same chrome:
  //   • Portrait  → wordmark + Add Chips / balance on top, nav stacked
  //                 below (the landscape design adapted to a narrow column).
  //   • Landscape → a single wide row (per the source design): wordmark + help
  //                 on the left, nav + Add Chips / balance / profile on
  //                 the right.
  // The lobby uses a separate <SaloonHeader>, so this only affects in-game.
  if (compact && isPortrait && tableOverlay) {
    // Portrait single-table view — the SAME floating-overlay language as the
    // landscape design (no solid header bar). The felt fills the viewport behind
    // it; gameId/game# meta is drawn on the felt by <TableGameInfo />. Layout:
    //   • top-left   → support
    //   • top-center → card-fan "Your Tables" switcher
    //   • top-right  → vertical stack: balance, Add Chips, Lobby, menu
    // Mirrors the compact+tableOverlay branch below, stacked for a narrow column.
    // Portrait shows only the table-switching fan — NO multi-table grid
    // ("All tables") button. The multi-table grid is a desktop-browser-only
    // feature (hidden on all mobile via the `isMobile` gate), so the GridView
    // entry point is omitted here regardless. The fan itself is the SAME
    // <YourTables> the lobby renders.
    // Portrait switcher is the compact "{N} TABLES" badge that expands the
    // open-table previews (per the mobile design). Reuses the SAME open-table
    // list and switch handler as the fan / nav chips. The Add Chips "+" stays
    // beside the balance — it is NOT part of the switcher.
    // NO entrance animation. The table strip is fixed chrome: the same buttons
    // in the same places whichever table you are on, so replaying a staggered
    // fan-in every time the player swipes is motion with no purpose — and the
    // header remounts on every table switch (it lives inside Game's
    // gameID-keyed provider subtree), so `animate` here meant "replay on every
    // swipe". `!stackTables` was also inverted against the prop's own contract:
    // animateIn documents itself as the SWITCHER DROPDOWN's open animation,
    // "off for the always-visible fan", and this passed true to exactly the
    // always-visible one (Jason 2026-08-16).
    const portraitSwitcher = renderOpenTables(false, selectorTrailing);
    // The mobile strip owns the whole band (cards anchored right, "+" drifting
    // left), so the band stops short of the measured corner clusters — the
    // newest card must never end up under Sit Out / Observe / Exit Table. The
    // stacked badge and the desktop fan row stay centered and unchanged.
    const insets = stripBandInsets(
      clusterReach.left,
      clusterReach.right,
      STRIP_EDGE_INSET_PORTRAIT
    );
    // The insets are viewport-absolute (measured reach already accounts for the
    // safe-area and each cluster's own offset), so they are NOT added to
    // SAFE_LEFT/SAFE_RIGHT — that would double-count the notch.
    // Even the last table has a status tile; keep it clear of corner controls.
    // Only a strip without any table cards can use the full screen width.
    const stripHasCards = activeGames.length > 0;
    const portraitCenterStyle =
      useStripNav && !stackTables
        ? {
            ...saloonPortraitStripBand,
            left: stripHasCards ? insets.left : 0,
            right: stripHasCards ? insets.right : 0
          }
        : saloonPortraitCenter;

    return (
      // The overlay root is the strip band's containing block AND the frame its
      // insets are measured in, so the layout is identical whether this header
      // is on screen or riding an off-screen carousel panel mid-switch.
      <div ref={setOverlayRootEl} style={saloonOverlayRoot}>
        {/* Table closing warning — derived from closing_after_hand flag, never
            overwritten by game actions unlike the msg field. Shown above the
            campaign ticker so it always stays visible during a hand. */}
        {Number(tableClosingAfterHand) === 1 && (
          <div style={saloonOverlayTickerBar}>
            <TableShutdownBanner message={TABLE_SHUTDOWN_MESSAGE} />
          </div>
        )}
        {/* Scrolling notification ticker at the top of the felt. Sits above the
            corner buttons in z-order, but withTickerShift below pushes them
            down to clear it — see tickerShift.ts. */}
        {showTableTicker && !Number(tableClosingAfterHand) && (
          <div style={saloonOverlayTickerBar}>
            <TickerBanner
              context='table'
              variant='outlawSolid'
              onVisibilityChange={handleTickerVisibleChange}
            />
          </div>
        )}
        {/* Top-left zone, stacked: hamburger (account/table menu) → Add Chips →
            support. The container is pass-through; each button re-enables
            pointer events. The hamburger's only content (menuView) is the
            player's identity/balance card plus a Cashier row — both member-only
            — so it's replaced for guests with a direct "back to lobby" action
            instead (same slot, same style) — the equivalent of the hamburger's
            "Go back to Lobby" row, without opening a menu full of member-only
            content. This keeps the table subscribed (unlike Exit Table, which
            leaves it). Support tap shows a login advisory for guests
            (handleOpenSupport). */}
        <div ref={setLeftClusterEl} style={withTickerShift(saloonPortraitTopLeft)}>
          {guest ? (
            <button
              aria-label='Back to lobby'
              onClick={returnToLobby}
              style={saloonIconBtnStyle}
              {...saloonHover}>
              <HomeOutlinedIcon style={{ fontSize: '1.15rem' }} />
            </button>
          ) : (
            <button
              aria-label='Open menu'
              onClick={handleOpenMenu}
              style={saloonIconBtnStyle}
              {...saloonHover}>
              <MenuIcon style={{ fontSize: '1.15rem' }} />
            </button>
          )}
          {/* Add Chips sits with the menu icons rather than in the top-right
              action stack, and takes the SAME icon-button treatment/size as the
              hamburger and support so the three read as one column. It is still
              UNMOUNTED whenever a top-up isn't currently available (observing /
              not seated / at max stack / mid-action) — no reserved placeholder
              (EN-10265), so support simply moves up into the gap. The trade-off
              is unchanged: the button appears/disappears at the edges of your
              own turn (the engine drops maximumTopUp → 0 while you act). The
              !guest guard is defense in depth (guests can't be seated) — see the
              "for a guest even if the seat data looks top-up eligible"
              regression test. */}
          {!guest && isCashTable && canTopUp && (
            <Tooltip title='Add Chips'>
              <button
                aria-label='Add chips'
                onClick={handleOpenAddChips}
                style={saloonIconBtnStyle}
                {...saloonHover}>
                <AddCircleOutlineIcon style={{ fontSize: '1.15rem' }} />
              </button>
            </Tooltip>
          )}
          <button
            aria-label='Open support'
            onClick={handleOpenSupport}
            style={saloonIconBtnStyle}
            {...saloonHover}>
            <HeadsetMicIcon style={{ fontSize: '1.05rem' }} />
            {supportUnread > 0 && (
              <span style={supportBadgeStyle}>{supportUnread > 9 ? '9+' : supportUnread}</span>
            )}
          </button>
        </div>

        {/* Top-center: open-table switcher (stacked badge) or spread fans */}
        {portraitSwitcher && (
          // Same chrome reservation as the landscape branch below. Portrait is
          // the viewport where the strip overlaps the top seat band hardest —
          // and it returns from here, so attaching the ref only in the landscape
          // branch left the reported phone case exactly as it was.
          <div ref={attachChromeInset} style={withTickerShift(portraitCenterStyle)}>
            {portraitSwitcher}
          </div>
        )}

        {/* Top-right: the table controls. Add Chips moved to the top-left icon
            column (with the hamburger and support); balance lives in the
            hamburger menu (EN parity). */}
        <div ref={setRightClusterEl} style={withTickerShift(saloonPortraitTopRight)}>
          {/* Engine-driven table controls (Sit Out / Exit Table) — per the
              Mobile.html design these live in the top-right stack, not the
              footer. Exit Table is shown here (default showExit) so players have a
              direct control on the table, alongside the hamburger menu's copy.
              Wrapped in a pointer-events:auto box because the cluster container is
              pass-through (so fans behind its empty areas stay clickable). */}
          {/* SitOut self-hides when not seated, so a guest sees only Exit Table
              here — letting an observer leave the table.

              Omitted when the felts are TILED: at two or more in view each tile
              carries its own cluster (Jason 2026-08-16 — "each table keeps its
              own controls"), and a copy up here would act on only one of them
              while looking like it governed the lot. */}
          {!tiled && (
            <div style={{ pointerEvents: 'auto' }}>
              <TableActions />
            </div>
          )}
        </div>

        {/* In-table hamburger menu (Go back to Lobby / Cashier / Table Settings /
            Table History / Exit Table) — member-only, see the hamburger button
            comment above. */}
        {!guest && <TableMenuPanel open={showTableMenu} onClose={() => setShowTableMenu(false)} />}
        {/* Kept mounted for the 'open-profile-panel' deep-link (e.g. KYC / profile
            edit) so those flows still work in-table; no longer opened by a pill. */}
        {!guest && (
          <AccountMenuPanel open={showProfileMenu} onClose={() => setShowProfileMenu(false)} />
        )}
      </div>
    );
  }

  // Landscape single-table view — transparent floating overlays on the felt
  // (no header bar). Layout per the Saloon design:
  //   • top-left   → support button (the gameId/game# meta sits to its right,
  //                  rendered on the felt by <TableGameInfo />)
  //   • top-center → "Your Tables" open-table switcher
  //   • top-right  → Add Chips / Lobby / menu, with the balance pill below
  // The engine-driven Exit / Sit Out / Stand Up cluster floats on the right rail
  // (see UserActions → TopAction), beneath this balance pill.
  if (compact && tableOverlay) {
    // Grid view is desktop-only AND needs room for two tables — omitted on
    // mobile and at one-table widths (where /grid would immediately hand off
    // back to single-table anyway, making the button a dead toggle).
    // The count control appears exactly when there is a choice to make: the
    // window fits more than one table AND the player has more than one open.
    // TableCountControl re-checks this itself and renders nothing otherwise;
    // this gate also drops the balancing spacer so the fans stay centred.

    // Narrow (mobile OR a one-table-wide desktop window) uses the SAME compact
    // strip as portrait (no room for the fan row + grid button). Wide web keeps
    // the always-visible fan row + All Tables grid button.
    // Center previews: stacked badge (stackTables on) or spread fans (off) —
    // shared with portrait via renderOpenTables. On wide desktop the "All
    // Tables" grid button flanks them; narrow shows the previews alone.
    // The count selector rides INSIDE the fan row as trailing content, so the
    // row's own slot geometry places it — see YourTables `trailing`.
    const previews = renderOpenTables(false, selectorTrailing); // see portraitSwitcher
    const openTableSwitcher = useStripNav
      ? previews
      : (activeGames.length > 0 || headerView === 'grid') && (
          // When stacked, the TableSwitcher's tap-to-expand dropdown renders
          // absolutely BELOW this row; the row's overflow:auto (for scrolling
          // wide spread-fan rows) would clip it, so let it overflow when stacked
          // (a single badge never needs horizontal scroll anyway).
          <div
            style={
              stackTables
                ? { ...saloonSwitcherRow, overflowX: 'visible', overflowY: 'visible' }
                : saloonSwitcherRow
            }>
            {/* The multi-tabling CLUSTER — same members, same order, same gap
                as the grid header's: add-table (leading, inside the fans), the
                fan tiles, then the count selector. See tableClusterMetrics. */}
            {/* The fan row places the selector itself (its slot geometry is
                what makes the spacing even). When there is no row to place it
                — a stacked switcher, or no fans to show — the selector still
                has to be reachable, so it falls back to standing alone. */}
            {previews || selectorTrailing}
          </div>
        );

    return (
      <div style={saloonOverlayRoot}>
        {/* Table closing warning — derived from closing_after_hand, not msg. */}
        {Number(tableClosingAfterHand) === 1 && (
          <div style={saloonOverlayTickerBar}>
            <TableShutdownBanner message={TABLE_SHUTDOWN_MESSAGE} />
          </div>
        )}
        {/* Scrolling notification ticker at the top of the felt. Sits above the
            corner buttons in z-order, but withTickerShift below pushes them
            down to clear it — see tickerShift.ts. */}
        {showTableTicker && !Number(tableClosingAfterHand) && (
          <div style={saloonOverlayTickerBar}>
            <TickerBanner
              context='table'
              variant='outlawSolid'
              onVisibilityChange={handleTickerVisibleChange}
            />
          </div>
        )}
        {/* Top-left: hamburger (account/table menu) + support. gameId/game# meta
            is drawn on the felt by TableGameInfo. The hamburger's only content
            (menuView) is the player's identity/balance card plus a Cashier
            row — both member-only — so it's replaced for guests with a direct
            "back to lobby" action instead (see the portrait branch's comment
            above for why). */}
        <div style={withTickerShift(saloonOverlayTopLeft)}>
          {guest ? (
            <button
              aria-label='Back to lobby'
              onClick={returnToLobby}
              style={saloonIconBtnStyle}
              {...saloonHover}>
              <HomeOutlinedIcon style={{ fontSize: '1.15rem' }} />
            </button>
          ) : (
            <button
              aria-label='Open menu'
              onClick={handleOpenMenu}
              style={saloonIconBtnStyle}
              {...saloonHover}>
              <MenuIcon style={{ fontSize: '1.15rem' }} />
            </button>
          )}
          {/* Add Chips sits under the hamburger here exactly as it does in
              portrait, so the icon column reads the same in both orientations
              (it used to live in the right-hand action rail in landscape only).
              Same eligibility gate and same unmount-when-ineligible behaviour:
              support simply moves up into the gap. */}
          {!guest && isCashTable && canTopUp && (
            <Tooltip title='Add Chips'>
              <button
                aria-label='Add chips'
                onClick={handleOpenAddChips}
                style={saloonIconBtnStyle}
                {...saloonHover}>
                <AddCircleOutlineIcon style={{ fontSize: '1.15rem' }} />
              </button>
            </Tooltip>
          )}
          <button
            aria-label='Open support'
            onClick={handleOpenSupport}
            style={saloonIconBtnStyle}
            {...saloonHover}>
            <HeadsetMicIcon style={{ fontSize: '1.05rem' }} />
            {supportUnread > 0 && (
              <span style={supportBadgeStyle}>{supportUnread > 9 ? '9+' : supportUnread}</span>
            )}
          </button>
        </div>

        {/* Top-center: open-table switcher ("Your Tables") */}
        {openTableSwitcher && (
          // Web scales the fan row up (1.25); the mobile badge stays unscaled so
          // it isn't oversized. Narrow + unstacked is the navigation strip, which
          // spans the band so it can pin its "+" left and its cards right.
          //
          // `chromeInsetRef` publishes this band's height so the felt can reserve
          // it rather than being painted over — see hooks/useChromeInset.
          <div
            ref={attachChromeInset}
            style={withTickerShift(
              useStripNav
                ? stackTables
                  ? saloonOverlayCenterMobile
                  : saloonOverlayStripBand
                : saloonOverlayDockBand
            )}>
            {openTableSwitcher}
          </div>
        )}

        {/* Add Chips is rendered in the floating right-rail action cluster
            (Game.tsx → TableActions showAddChips) so it groups with Sit Out /
            Observe / Exit instead of floating alone in the top-right corner. */}

        {/* In-table hamburger menu (Go back to Lobby / Cashier / Table Settings /
            Table History / Exit Table) — member-only, see the hamburger button
            comment above. */}
        {!guest && <TableMenuPanel open={showTableMenu} onClose={() => setShowTableMenu(false)} />}
        {/* Kept mounted for the 'open-profile-panel' deep-link; no longer opened
            by a balance pill. */}
        {!guest && (
          <AccountMenuPanel open={showProfileMenu} onClose={() => setShowProfileMenu(false)} />
        )}
      </div>
    );
  }

  // Landscape in-game header — single wide Saloon row.
  if (compact) {
    return (
      <div style={saloonHeaderWrapper}>
        {/* The row clips so a wide fan strip scrolls inside the dock instead of
            pushing out the balance/profile. Stacked, the dock is one badge whose
            previews drop BELOW the row, so the row must not clip them. */}
        <div
          style={stackTables ? { ...saloonLandscapeRow, overflow: 'visible' } : saloonLandscapeRow}>
          <div style={gridZoneSideStyle}>
            {/* Branding doubles as navigation: the top-left wordmark returns to
                the lobby — the most learned "go home" pattern there is — which
                is what let the Lobby button leave this header entirely (Jason
                2026-08-18). That freed ~78px, which is what keeps the dock's
                count picker clear of the balance at the 796px grid floor: with
                three content-sized sections plus the Lobby chip, the dock's
                picker slid under the balance pill at ~800px. The dock's own "+"
                (add table) is the second lobby route below the table cap,
                matching the single-table strip. */}
            <button
              onClick={returnToLobby}
              aria-label='Return to lobby'
              title='Lobby'
              style={logoLobbyButton}
              {...saloonHover}>
              <img src={Logo} alt='Hijack Poker' style={gridLogoStyle} />
            </button>

            {/* Support / help — icon-only. Tooltip stays for hover-on-real-
                pointer discovery; touch + focus listeners disabled (EN-10433
                pattern). */}
            <Tooltip title='Support Chat' disableTouchListener disableFocusListener>
              <button
                aria-label='Open support'
                onClick={handleOpenSupport}
                // Single-view support design (design owner 8/20): the quiet
                // icon chrome, not the rust CTA block — support is a utility,
                // and the grid should read identically to single view.
                style={saloonIconBtnStyle}
                {...saloonHover}>
                <HeadsetMicIcon style={{ fontSize: '1.05rem' }} />
                {supportUnread > 0 && (
                  <span style={supportBadgeStyle}>{supportUnread > 9 ? '9+' : supportUnread}</span>
                )}
              </button>
            </Tooltip>
          </div>

          {/* Centre zone: the table dock (grid) or the chip row (elsewhere). */}
          {showNavRow && navChips}

          {headerView !== 'grid' && <div style={{ flex: 1 }} />}

          <div style={{ ...gridZoneSideStyle, justifyContent: 'flex-end' }}>
            {/* Add Chips — hidden in multi-table (grid) view: each cash cell has
              its own table-scoped Add Chips there, so this global cashier
              shortcut would be redundant/confusing. */}
            {!guest && headerView !== 'grid' && (
              <button
                onClick={() => handleOpenCashier('deposit')}
                style={saloonHeaderBtn}
                {...saloonHover}>
                Add Chips
              </button>
            )}

            {guest ? (
              /* Guests have no balance/profile — offer the auth path instead. */
              <GuestCtaButton label={GUEST_CTA.header} />
            ) : (
              <>
                {/* Balance pill — cash over promo */}
                <button
                  onClick={() => handleOpenCashier()}
                  style={saloonBalancePill}
                  aria-label='Open cashier'
                  {...saloonHover}>
                  <span style={saloonCashStyle}>{currencyFormatter(userBalance)}</span>
                  <Tooltip
                    title={`Promo balance: ${formattedPromoBalance}. Applicable solely for cash game charges.`}
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}>
                    <span
                      aria-label={`Promo balance ${formattedPromoBalance}`}
                      onClick={(event) => event.stopPropagation()}
                      style={saloonPromoStyle}>
                      {formattedPromoBalance}
                    </span>
                  </Tooltip>
                </button>

                {/* Profile */}
                <AvatarFrame tier={streakFrame} size={42}>
                  <button
                    aria-label='Open profile'
                    onClick={handleToggleProfile}
                    style={streakFrame ? saloonProfileBtnFramed : saloonProfileBtn}
                    {...saloonHover}>
                    {playerAvatar ? (
                      <img
                        src={`https://media.${APP_DOMAIN}/images/${playerAvatar}`}
                        alt=''
                        style={saloonProfileAvatar}
                      />
                    ) : (
                      <AccountCircleIcon style={{ fontSize: '1.5rem' }} />
                    )}
                  </button>
                </AvatarFrame>
              </>
            )}
          </div>

          {/* Grid (multi-table) view uses the SAME account menu as every other
              view so the profile experience is consistent — it previously
              opened the old tabbed ProfilePanel here. */}
          {!guest && (
            <AccountMenuPanel open={showProfileMenu} onClose={() => setShowProfileMenu(false)} />
          )}
        </div>

        <TopScrollingNotificationBanner compact={compact} />
      </div>
    );
  }

  return (
    <div style={compact ? compactHeaderWrapper : headerWrapper}>
      {/* Row 1: logo + balance + profile (portrait stacks nav below) */}
      <div style={compact ? compactTopRow : topRow}>
        <img src={Logo} alt='Hijack' style={compact ? compactLogoStyle : logoStyle} />

        {/* Navigation inline on landscape only */}
        {!isPortrait && showNavRow && navChips}

        <div style={{ flex: 1 }} />

        {/* Balance - tap to open cashier */}
        <div style={{ ...balanceStyle, cursor: 'pointer' }} onClick={() => handleOpenCashier()}>
          <Tooltip title='Tap to open cashier'>
            <span style={cashBalanceStyle}>{currencyFormatter(userBalance)}</span>
          </Tooltip>
          <span style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
          <Tooltip
            title={`Promo balance: ${formattedPromoBalance}. Applicable solely for cash game charges.`}
            enterTouchDelay={0}
            leaveTouchDelay={3000}>
            <span
              aria-label={`Promo balance ${formattedPromoBalance}`}
              onClick={(event) => event.stopPropagation()}
              style={promoBalanceStyle}>
              {formattedPromoBalance}
            </span>
          </Tooltip>
        </div>

        {/* Support — icon-only. Tooltip stays for hover-on-real-pointer
            discovery; touch + focus listeners disabled (EN-10433 pattern). */}
        <Tooltip title='Support Chat' disableTouchListener disableFocusListener>
          <button aria-label='Open support' onClick={handleOpenSupport} style={supportBtnStyle}>
            <HeadsetMicIcon style={{ fontSize: '1.1rem' }} />
            {supportUnread > 0 && (
              <span style={supportBadgeStyle}>{supportUnread > 9 ? '9+' : supportUnread}</span>
            )}
          </button>
        </Tooltip>

        <NotificationCenter />

        {/* Profile */}
        <button aria-label='Open profile' onClick={handleToggleProfile} style={profileBtnStyle}>
          {playerAvatar ? (
            <img
              src={`https://media.${APP_DOMAIN}/images/${playerAvatar}`}
              alt=''
              style={profileAvatarStyle}
            />
          ) : (
            <AccountCircleIcon style={{ fontSize: '1.8rem' }} />
          )}
        </button>
        <AccountMenuPanel open={showProfileMenu} onClose={() => setShowProfileMenu(false)} />
      </div>

      <TopScrollingNotificationBanner compact={compact} />

      {/* Row 2: filters (lobby only).
          Two layouts:
            • Landscape — single wrapping row (existing).
            • Portrait — two clean rows: a segmented tab strip + filter
              icon button on top, and a horizontally scrollable sub-pills
              row below. No wrapping; sub-pills slide instead of stacking. */}
      {!compact && !useCompactFilterLayout && (
        <div style={filterRow}>
          {/* Table type tabs */}
          <div style={tabsContainer}>
            {tableTypes.map((tt) => (
              <button
                key={tt.key}
                onClick={() => dispatch(updateTableType(tt.key))}
                style={{
                  ...tabStyle,
                  ...(tableType === tt.key ? activeTabStyle : {})
                }}>
                {tt.icon}
                {tt.label}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div style={dividerStyle} />

          {/* Game mode pills (only for cash games) — multi-select */}
          {tableType === TableType.CashGames && activeGameModes && onToggleGameMode && (
            <div style={pillsContainer}>
              {gameModes.map((mode) => (
                <button
                  key={mode.key}
                  onClick={() => onToggleGameMode(mode.key)}
                  style={{
                    ...pillStyle,
                    ...(activeGameModes.has(mode.key) ? activePillStyle : {})
                  }}>
                  {mode.icon}
                  {mode.label}
                </button>
              ))}
            </div>
          )}

          {/* Tournament-type pills (only on Tournaments tab) — multi-select */}
          {tableType === TableType.Standard && activeTournamentTypes && onToggleTournamentType && (
            <div style={pillsContainer}>
              {tournamentTypeOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => onToggleTournamentType(opt.key)}
                  style={{
                    ...pillStyle,
                    ...(activeTournamentTypes.has(opt.key) ? activePillStyle : {})
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Divider */}
          {tableType === TableType.CashGames && <div style={dividerStyle} />}

          {/* Filter toggles (only for cash games) */}
          {tableType === TableType.CashGames && (
            <div style={togglesContainer}>
              <button
                onClick={onToggleHideFull}
                style={{ ...toggleStyle, ...(hideFull ? activeToggleStyle : {}) }}>
                Hide Full
              </button>
            </div>
          )}

          {/* Spacer pushes Filters button to the right when room allows */}
          <div style={{ flex: 1 }} />

          {/* Open advanced filters modal — available on every tab */}
          {onOpenFilters && (
            <Tooltip title='Filter tables'>
              <button
                onClick={onOpenFilters}
                style={filtersBtnStyle}
                aria-label='Open table filters'>
                <TuneIcon style={{ fontSize: '1rem' }} />
                <span>Filters</span>
              </button>
            </Tooltip>
          )}
        </div>
      )}

      {/* Portrait + narrow-landscape filter rows — premium mobile layout */}
      {!compact && useCompactFilterLayout && (
        <>
          {/* Row 2A: segmented tabs + filter icon */}
          <div style={mobileTabsRow}>
            <div style={mobileSegmentedTabs}>
              {tableTypes.map((tt) => {
                const active = tableType === tt.key;
                return (
                  <button
                    key={tt.key}
                    onClick={() => dispatch(updateTableType(tt.key))}
                    style={{
                      ...mobileSegmentedTab,
                      ...(active ? mobileSegmentedTabActive : {})
                    }}>
                    {tt.icon}
                    <span>{tt.label}</span>
                  </button>
                );
              })}
            </div>

            {onOpenFilters && (
              <button
                onClick={onOpenFilters}
                style={mobileFiltersIconBtn}
                aria-label='Open table filters'>
                <TuneIcon style={{ fontSize: '1.05rem' }} />
              </button>
            )}
          </div>

          {/* Row 2B: scrollable sub-pills (only when the active tab has any) */}
          {((tableType === TableType.CashGames && activeGameModes && onToggleGameMode) ||
            (tableType === TableType.Standard && activeTournamentTypes && onToggleTournamentType) ||
            tableType === TableType.CashGames) && (
            <div style={mobileSubPillsScroll}>
              <div style={mobileSubPillsTrack}>
                {tableType === TableType.CashGames &&
                  activeGameModes &&
                  onToggleGameMode &&
                  gameModes.map((mode) => {
                    const active = activeGameModes.has(mode.key);
                    return (
                      <button
                        key={mode.key}
                        onClick={() => onToggleGameMode(mode.key)}
                        style={{
                          ...mobileSubPill,
                          ...(active ? mobileSubPillActive : {})
                        }}>
                        {mode.icon}
                        {mode.label}
                      </button>
                    );
                  })}

                {tableType === TableType.Standard &&
                  activeTournamentTypes &&
                  onToggleTournamentType &&
                  tournamentTypeOptions.map((opt) => {
                    const active = activeTournamentTypes.has(opt.key);
                    return (
                      <button
                        key={opt.key}
                        onClick={() => onToggleTournamentType(opt.key)}
                        style={{
                          ...mobileSubPill,
                          ...(active ? mobileSubPillActive : {})
                        }}>
                        {opt.label}
                      </button>
                    );
                  })}

                {/* Hide Full / Hide Empty live in the same scroll lane on Cash so
                    everything is one tappable horizontal lane on mobile. */}
                {tableType === TableType.CashGames && (
                  <>
                    <div style={mobileSubPillSeparator} />
                    <button
                      onClick={onToggleHideFull}
                      style={{
                        ...mobileSubPill,
                        ...(hideFull ? mobileSubPillActive : {})
                      }}>
                      Hide Full
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Row 3 (portrait): current tables nav — hidden when on lobby with no tables */}
      {isPortrait && showNavRow && <div style={portraitNavRow}>{navChips}</div>}
    </div>
  );
};

const TopScrollingNotificationBanner = ({ compact = false }: { compact?: boolean }) => {
  const campaigns = useAppSelector((state) => (state as any).notifications?.campaigns || []);
  const context = compact ? 'table' : 'lobby';

  const hasActiveCampaigns = campaigns.some(
    (c: any) =>
      !c.rotation_paused &&
      c.messages?.length > 0 &&
      (!c.contexts || c.contexts.includes(context) || c.contexts.includes('all'))
  );

  if (!hasActiveCampaigns) return null;

  return <TickerBanner context={context} variant='outlawSolid' />;
};

export default TableHeader;

// NOTE: dropped `backdrop-filter: blur(...)` from these wrappers. With a 0.95 alpha
// background it contributed almost nothing visually but forced the whole header into
// a composited layer, which downgrades subpixel-antialiased text to grayscale on
// Chromium/WebKit and made every label/pill in the filter row look slightly blurry.
const headerWrapper: CSSProperties = {
  position: 'relative',
  zIndex: 50,
  backgroundColor: 'rgba(22, 18, 14, 0.95)',
  borderBottom: '1px solid rgba(212, 168, 83, 0.12)'
};

const compactHeaderWrapper: CSSProperties = {
  position: 'relative',
  zIndex: 50,
  backgroundColor: 'rgba(22, 18, 14, 0.95)',
  borderBottom: '1px solid rgba(212, 168, 83, 0.12)'
};

const topRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem 0.75rem',
  flexWrap: 'wrap'
};

const compactTopRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.4rem 0.75rem',
  flexWrap: 'wrap'
};

const compactLogoStyle: CSSProperties = {
  width: 60,
  opacity: 0.85
};

/* ── Saloon in-game header (portrait table view) ──
   Adapted from the landscape `tp-lh-*` design. Navy gradient bar with an
   orange under-rule; Rye wordmark on the left, Lobby / Add Chips / balance
   pill / support / profile on the right. Per the perf guide: static
   box-shadow, transform-only hover (gated behind useSupportsHover). */
const saloonHeaderWrapper: CSSProperties = {
  position: 'relative',
  zIndex: 50,
  // EXPERIMENT (design owner, 2026-08-20 — easy revert: restore the navy
  // gradient below). The grid's header was the one blue element on an
  // otherwise warm-dark table room, which is the single-vs-multi header
  // mismatch Cory flagged (8/19). Matching the room's own background makes the
  // grid read as the same PLACE as single-table view; the hairline keeps the
  // band legible as chrome. Was:
  //   background: linear-gradient(180deg, navy800, navy950)
  //   borderBottom: 1px solid rgba(232,130,31,0.32)
  background: 'linear-gradient(180deg, #1c1a14 0%, #16140f 100%)',
  borderBottom: '1px solid rgba(212, 168, 83, 0.18)',
  boxShadow: '0 2px 10px rgba(0,0,0,0.45)'
};

/**
 * Tallest the compact header band ever needs to be, derived from the tile
 * geometry it contains: 22px (the grid nav row's 11px vertical padding) + 12px
 * (the fan list's 8/4 padding) + 44px (a `.hand`) + ~17.6px (this row's own
 * 0.55rem padding) — call it 96.
 */
export const COMPACT_HEADER_MIN_HEIGHT = 96;

// Published to CSS so a stylesheet that has to clear the header band stays in
// step with this constant instead of copying its value. The server-timeout
// toast is the case that needs it: it portals to <body> (EN-10538 AC2), so it
// cannot inherit a custom property set on the header itself, and it hardcoded
// `96px` — which would silently drift onto or off the nav strip if this moved,
// with nothing failing (JRam review, #3720). Set at import time on :root;
// guarded for non-DOM environments.
if (typeof document !== 'undefined') {
  document.documentElement.style.setProperty(
    '--compact-header-height',
    `${COMPACT_HEADER_MIN_HEIGHT}px`
  );
}

/**
 * Side zones of the grid header row. On the GRID the row is three zones so the
 * table dock sits at the TRUE centre of the band — matching where the
 * single-table overlay centres it — instead of being left-aligned after Lobby
 * (Jason 2026-08-17). `flex: 1 1 0` gives both zones an equal claim on the
 * free space, which is what centres the dock between them.
 *
 * Deliberately NO `minWidth: 0` here. Flexbox's default min-width:auto floors
 * each zone at its content, and that floor IS the collision guard: when a
 * narrow window leaves less than an equal share on one side, that zone stops
 * shrinking and the dock is nudged off-centre into the other side's slack
 * instead of being painted over the zone's buttons. With `minWidth: 0` the
 * left zone kept shrinking under wordmark + Support + Lobby, and the first
 * fan tile covered the Lobby button at ~800px (Jason 2026-08-18).
 *
 * Degradation ladder, widest → narrowest: dock truly centred → dock nudged
 * toward the roomier side → dock scrolls internally. The last step is the
 * dock's own contract (`navContainer`: flex 0 1 auto + minWidth 0 + overflowX
 * auto), which is also what stops the row — overflow:hidden — from ever
 * clipping the balance/profile: the zones' floors together stay far below any
 * width the grid can exist at (>=796px).
 */
export const gridZoneSideStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  flex: '1 1 0'
};

export const saloonLandscapeRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  padding: '0.55rem 1rem',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  boxSizing: 'border-box',
  /**
   * PERSISTENT CHROME MUST NOT RESIZE WITH TRANSIENT TABLE STATE.
   *
   * This band had no height of its own — it was whatever its tallest child
   * happened to be that frame. Everything below is laid out from its bottom
   * edge, including each grid cell's Sit Out / Exit Table rail (absolute to its
   * felt's top) and the table-id line. So any child growing by a few pixels
   * moved the controls on EVERY table at once, and with four felts up some
   * table is nearly always entering or leaving turn — which is the row visibly
   * jittering up and down, and the table-id line clipping against the band
   * (Jason 2026-08-17).
   *
   * A FLOOR, not a fixed height, on purpose: it absorbs the variation without
   * ever being able to clip a child that genuinely needs more room — the new
   * failure mode a hard `height` would introduce for a long balance or a wide
   * fan strip.
   */
  minHeight: COMPACT_HEADER_MIN_HEIGHT
};

// Wordmark-as-button wrapper: strips button chrome so the wordmark renders
// exactly as the plain <img> did, while giving the lobby route a real focusable
// control (cursor, aria-label, hover feedback via saloonHover).
const logoLobbyButton: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  background: 'none',
  border: 0,
  padding: 0,
  flexShrink: 0,
  cursor: 'pointer',
  transition: 'transform 0.12s ease',
  pointerEvents: 'auto'
};

// Grid header brand logo — the full HIJACK POKER wordmark image (matches the
// lobby SaloonHeader), replacing the old small Rye text wordmark. Width auto
// keeps its aspect ratio at the taller grid header height.
const gridLogoStyle: CSSProperties = {
  height: 40,
  width: 'auto',
  flexShrink: 0,
  objectFit: 'contain',
  filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.55))'
};

// Grid header icon button (support) — brown leather to match the saloon Add
// Chips / lobby buttons, instead of the faint shared saloonIconBtn used by the
// single-table overlay header. position:relative anchors the unread badge.

const saloonHeaderBtn: CSSProperties = {
  padding: '0.35rem 0.45rem',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(232,130,31,0.32)',
  borderRadius: '5px',
  color: saloonTokens.bone,
  fontFamily: saloonFonts.numeric, // 'Oswald'
  fontWeight: 600,
  fontSize: '0.62rem',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  minHeight: '32px',
  transition: 'transform 0.12s ease',
  // Re-enable hits when the parent cluster is pass-through (portrait overlay).
  pointerEvents: 'auto'
};

const saloonBalancePill: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'center',
  lineHeight: 1.1,
  padding: '0.25rem 0.7rem',
  background: 'rgba(0,0,0,0.6)',
  border: '1px solid rgba(232,130,31,0.45)',
  borderRadius: '8px',
  fontFamily: saloonFonts.numeric,
  fontWeight: 700,
  cursor: 'pointer',
  flexShrink: 0,
  minHeight: '40px',
  transition: 'transform 0.12s ease',
  pointerEvents: 'auto'
};

const saloonCashStyle: CSSProperties = {
  fontSize: '0.82rem',
  color: saloonTokens.bone,
  whiteSpace: 'nowrap'
};

const saloonPromoStyle: CSSProperties = {
  fontSize: '0.68rem',
  color: saloonTokens.orange300,
  cursor: 'help'
};

const saloonProfileBtn: CSSProperties = {
  width: 42,
  height: 42,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: `linear-gradient(180deg, ${saloonTokens.rust700}, ${saloonTokens.rust800})`,
  // Gold ring (grid header redesign) — a thicker orange border plus a faint
  // outer gold halo so the avatar reads as the branded profile marker.
  border: '2px solid rgba(245,152,56,0.85)',
  boxShadow: '0 0 0 1px rgba(255,208,137,0.35), 0 2px 6px rgba(0,0,0,0.5)',
  color: saloonTokens.bone2,
  cursor: 'pointer',
  padding: 0,
  overflow: 'hidden',
  transition: 'transform 0.12s ease'
};

/** The streak frame stands in for the orange ring and halo. */
const saloonProfileBtnFramed: CSSProperties = {
  ...saloonProfileBtn,
  border: 'none',
  boxShadow: 'none'
};

const saloonProfileAvatar: CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  objectFit: 'cover'
};

/* ── Saloon landscape floating overlays (single-table view) ──
   The wrapper is a zero-footprint absolute layer so the felt fills the full
   viewport height (design centers it at 50%). It ignores pointer events; each
   floating group re-enables them so the felt stays interactive between them. */
// Device safe-area insets. The overlay layer is full-bleed (position:absolute,
// top/left/right:0) so its absolutely-positioned children anchor to the padding
// box and DON'T inherit the page's safe-area paddingTop — the floating button
// clusters would otherwise sit under the notch / status bar. Each top cluster
// adds the relevant inset to its own offset so it clears the notch on every
// orientation (matches how SaloonLobby honors safe areas).
const SAFE_TOP = 'env(safe-area-inset-top, 0px)';
const SAFE_LEFT = 'env(safe-area-inset-left, 0px)';
const SAFE_RIGHT = 'env(safe-area-inset-right, 0px)';

const saloonOverlayRoot: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 50,
  // The layer ignores pointer events so the felt stays interactive; each
  // floating group re-enables them. Full-height (not height:0) so bottom-
  // anchored children like the promo widget position against the viewport.
  pointerEvents: 'none'
};

// Full-width ticker pinned to the top of the felt overlay, above the corner
// button groups (higher zIndex) — withTickerShift pushes those down to clear
// it. pointerEvents auto since messages can carry a click action. Carries the
// safe-area inset so it clears a notch.
const saloonOverlayTickerBar: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  paddingTop: SAFE_TOP,
  zIndex: 60,
  pointerEvents: 'auto'
};

// The corner overlay groups are scaled up 25% (static transform). Each anchors
// its transform-origin to its own corner so the group grows inward and stays
// pinned to the edge instead of drifting.
const saloonOverlayTopLeft: CSSProperties = {
  position: 'absolute',
  top: `calc(${SAFE_TOP} + 8px)`,
  left: `calc(${SAFE_LEFT} + 12px)`,
  display: 'flex',
  // Column so the support headset stacks directly UNDER the hamburger.
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 8,
  transform: 'scale(1.25)',
  transformOrigin: 'top left',
  pointerEvents: 'auto'
};

// LEGACY BASE — kept only as the spread source for the two mobile variants
// below (badge band / strip band), which override its top and transform. The
// wide single-table view no longer uses it directly: it renders the dock in
// saloonOverlayDockBand instead (EN-10545, one-dock parity).
const saloonOverlayCenter: CSSProperties = {
  position: 'absolute',
  // Moved down (was +8px) so the enlarged fan framing boxes clear the top edge
  // instead of overlapping it in the single-table web overlay.
  top: `calc(${SAFE_TOP} + 20px)`,
  left: '50%',
  transform: 'translateX(-50%) scale(1.25)',
  transformOrigin: 'top center',
  maxWidth: '46%',
  pointerEvents: 'auto'
};

/**
 * ONE DOCK, TWO SURFACES (EN-10545 — Jason 8/21). The single-table view's dock
 * band is an invisible replica of the grid bar's band box, so the dock lands
 * at IDENTICAL pixels on both surfaces and the table↔grid cut reads as one
 * persistent dock that never re-renders:
 *
 *   • same height — COMPACT_HEADER_MIN_HEIGHT, the bar's floor — with the
 *     cluster centered in it, which is exactly how the bar places its centre
 *     zone. Same box + same centering + same content ⇒ same y, by
 *     construction rather than by a hand-tuned offset.
 *   • same scale — the legacy 1.25 up-scale is gone. The grid's size is the
 *     designed size (design owner 8/21): tiles read fine at 0.85·58px, and the
 *     smaller band gives the felt back ~14px of vertical room.
 *   • same width cap — DOCK_BAND_MAX_WIDTH on both surfaces, so YourTables'
 *     fit-to-band scale computes identically at every viewport width (two
 *     different caps meant two different fitted sizes at tight widths).
 *
 * The band itself is pass-through; the switcher row re-enables pointer events
 * (a 96px-tall hit area would otherwise eat felt clicks around the cluster).
 * Known, accepted divergence: withTickerShift can push this band down while
 * the dismissible ticker is up — the bar hosts the ticker below itself instead.
 */
export const saloonOverlayDockBand: CSSProperties = {
  position: 'absolute',
  top: SAFE_TOP,
  left: '50%',
  transform: 'translateX(-50%)',
  height: COMPACT_HEADER_MIN_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: DOCK_BAND_MAX_WIDTH,
  pointerEvents: 'none'
};

// Mobile landscape hosts the compact "{N} TABLES" badge instead of the fan row,
// so it skips the web 1.25 up-scale (which made the badge oversized). Pulled
// higher than the shared SAFE_TOP + 8px so the badge's top edge lines up with
// the corner buttons (Lobby / cashier), which scale 1.25 from their top edge
// and so sit slightly higher than an unscaled element at the same offset.
const saloonOverlayCenterMobile: CSSProperties = {
  ...saloonOverlayCenter,
  top: `calc(${SAFE_TOP} - -4px)`,
  transform: 'translateX(-50%)'
};

// How far the navigation strip's pinned edges stay clear of the screen edges,
// so the "+" (left) and the newest card (right) don't slide under the floating
// corner clusters (menu/support on the left; Add Chips / table actions on the
// right). Landscape's clusters are scaled 1.25, hence the wider inset.
const STRIP_EDGE_INSET_PORTRAIT = 56;
const STRIP_EDGE_INSET_LANDSCAPE = 76;
// Clearance above the strip, on top of any safe-area inset. A card fan's
// framing box and trophy badge overhang the fan by ~8px, and the panel is
// overflow:hidden — so with a zero inset (emulation, non-notched devices) a
// tighter offset clips the tops of the cards.
const STRIP_BAND_MIN_TOP = 12;

// Mobile landscape strip band — full-width (minus the corner-cluster insets)
// instead of the centered, max-width badge band, so the strip's own
// space-between layout decides where its ends sit.
const saloonOverlayStripBand: CSSProperties = {
  ...saloonOverlayCenterMobile,
  // The badge band sits at safe-area-top + 4px, which is fine for a compact
  // pill but not for the strip: a card fan's framing box (and its trophy badge)
  // overhang the fan itself by ~8px, and the panel this all lives in is
  // overflow:hidden — so with the safe-area inset at ZERO, as it is in browser
  // mobile emulation and on any non-notched device, the tops of the cards get
  // clipped by the panel edge. Clearance that doesn't depend on the inset.
  top: `calc(${SAFE_TOP} + ${STRIP_BAND_MIN_TOP}px)`,
  left: `calc(${SAFE_LEFT} + ${STRIP_EDGE_INSET_LANDSCAPE}px)`,
  right: `calc(${SAFE_RIGHT} + ${STRIP_EDGE_INSET_LANDSCAPE}px)`,
  transform: 'none',
  maxWidth: 'none',
  display: 'block',
  // The band itself stays pass-through; the strip's buttons re-enable clicks.
  pointerEvents: 'none'
};

const saloonSwitcherRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  // Center the [grid button + fans] as one group (the fans no longer self-shift
  // to page center — see autoCenter=false — so the button stays beside them).
  justifyContent: 'center',
  gap: '1.5rem',
  // Fully visible (was overflowX:auto): `overflow-x: auto` forces overflow-y to
  // clip too, which cut off the top of the enlarged fan framing boxes / the
  // trophy badge in the single-table web overlay. BareFans already fits + scales
  // the row so it never overflows horizontally, so no scroll lane is needed.
  overflow: 'visible',
  // The dock band wrapper is pass-through (see saloonOverlayDockBand) — the
  // row is where clicks become real again.
  pointerEvents: 'auto'
};

/* ── Saloon portrait floating overlays (single-table view) ──
   Same language as the landscape overlay, stacked for a narrow phone column.
   No 1.25× scale (the viewport is already tight). Each group re-enables pointer
   events on the otherwise pass-through saloonOverlayRoot layer. */
const saloonPortraitTopLeft: CSSProperties = {
  position: 'absolute',
  top: `calc(${SAFE_TOP} + 14px)`,
  left: `calc(${SAFE_LEFT} + 10px)`,
  display: 'flex',
  // Column so the support headset stacks directly UNDER the hamburger.
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 6,
  // Container is pass-through; only the actual buttons (which set pointerEvents
  // auto) capture clicks. This keeps a centered fan that tucks behind the
  // cluster's empty gaps clickable instead of being blanket-blocked.
  pointerEvents: 'none',
  // Above the centered fan band (zIndex 1) so the buttons stay on top.
  zIndex: 2
};

const saloonPortraitCenter: CSSProperties = {
  position: 'absolute',
  top: `calc(${SAFE_TOP} + 20px)`,
  // Full width + justify-center so the compact count badge sits at the TRUE
  // screen center, rather than the midpoint of the gap between the side
  // clusters (which is offset left
  // because the right cluster is wider). The badge is narrow and the expanded
  // previews anchor to the badge (not this band), so a centered badge never
  // collides with the corner clusters. pointerEvents is none on the band (so it
  // doesn't blanket the clusters); the badge re-enables it.
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none',
  // Sits beneath the corner button clusters.
  zIndex: 1
};

// Portrait strip band — stretched across the viewport (minus the corner-cluster
// insets, measured at runtime) so the strip's group can spread out, and pulled
// UP to the corner clusters' line: the gameId/game# meta that used to sit above
// it has moved to the felt's bottom-right corner (TableGameInfo), so the strip
// now owns the whole top band and its cards get the vertical room.
// left/right are overridden per render from the measured cluster reach; these
// are the pre-measurement fallbacks.
const saloonPortraitStripBand: CSSProperties = {
  ...saloonPortraitCenter,
  // Same clearance rule as landscape — see STRIP_BAND_MIN_TOP.
  top: `calc(${SAFE_TOP} + ${STRIP_BAND_MIN_TOP}px)`,
  left: STRIP_EDGE_INSET_PORTRAIT,
  right: STRIP_EDGE_INSET_PORTRAIT,
  display: 'block'
};

const saloonPortraitTopRight: CSSProperties = {
  position: 'absolute',
  top: `calc(${SAFE_TOP} + 14px)`,
  right: `calc(${SAFE_RIGHT} + 10px)`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 6,
  // Pass-through container; the buttons inside re-enable pointer events, so the
  // centered fans behind the cluster's empty areas remain clickable.
  pointerEvents: 'none',
  // Above the centered fan band (zIndex 1) so the buttons stay on top.
  zIndex: 2
};

const portraitNavRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '0.35rem 0.75rem',
  borderTop: '1px solid rgba(212, 168, 83, 0.08)',
  overflowX: 'auto',
  scrollbarWidth: 'none'
};

/**
 * The multi-tabling cluster's own row: add-table, fan tiles, count selector.
 *
 * ONE gap between all of them (CLUSTER_GAP). The selector previously sat far
 * off to the right of the fans while add-table hugged them, so the group read
 * as two separate things — the fix is that they share a container and a spacing
 * value rather than each carrying its own margin.
 */

/**
 * Hairline between the fan tiles and the count selector.
 *
 * The selector is the only member of the cluster that is not a table, so it
 * gets a separator rather than just more air — which also means the air either
 * side of it can stay equal to the gap between the tiles themselves. Margins are
 * TILE_GAP on both sides, so tile → line → selector reads at the same rhythm as
 * "+" → tile → tile, and the whole run from the "+" to the selector is one
 * evenly-spaced component at every viewport width (Jason 2026-08-17).
 */
const clusterDivider: CSSProperties = {
  width: 1,
  alignSelf: 'stretch',
  // Left margin is 0: the fan row already placed this at TILE_GAP from the last
  // tile's drawn edge (see YourTables `trailing`). Only the right side — line to
  // selector — is this component's to space.
  margin: `6px ${TILE_GAP}px 6px 0`,
  // GREY, not the header's orange hairline (Jason 2026-08-17). The orange is
  // the app's accent and reads as a control's edge; this line is punctuation
  // between two parts of one component, and should recede rather than assert.
  background: 'rgba(241,227,199,0.22)',
  flexShrink: 0
};

// Exported for tests: the dock must stay the SHRINKABLE element of the grid
// header row (see gridZoneSideStyle) — flooring it would hand the deficit to
// the row's overflow:hidden, which clips the balance/profile instead.
export const navContainer: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  minWidth: 0,
  flex: '0 1 auto'
};

const navChipStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.5rem 1rem',
  // Longhand (not the `border` shorthand) so the active/turn overrides below
  // can change `borderColor` alone without React warning about removing a
  // longhand property while a conflicting shorthand is set.
  borderWidth: '1px',
  borderStyle: 'solid',
  // Base chip = brown leather (the Lobby button in the grid header redesign).
  borderColor: 'rgba(232,130,31,0.4)',
  borderRadius: '10px',
  background: `linear-gradient(180deg, ${saloonTokens.rust700}, ${saloonTokens.rust800})`,
  color: saloonTokens.bone,
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s ease',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  minHeight: '40px',
  boxShadow: '0 2px 6px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,180,92,0.12)'
};

// Active chip = "All Tables" while in grid view: teal outline on a dark fill,
// distinct from the brown Lobby chip.
const navChipActiveStyle: CSSProperties = {
  background: 'rgba(16, 74, 70, 0.4)',
  borderColor: 'rgba(72, 190, 176, 0.6)',
  color: '#7fe0d0',
  boxShadow: '0 0 12px rgba(72, 190, 176, 0.18)'
};

const navChipTurnStyle: CSSProperties = {
  borderColor: 'rgba(233, 140, 38, 0.5)',
  color: '#E98C26'
};

const navChipTypeStyle: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255, 255, 255, 0.35)',
  textTransform: 'uppercase'
};

const turnDotStyle: CSSProperties = {
  width: 7,
  height: 6,
  borderRadius: '50%',
  backgroundColor: '#E98C26',
  flexShrink: 0
};

const filterRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.4rem 0.75rem',
  borderTop: '1px solid rgba(212, 168, 83, 0.08)',
  flexWrap: 'wrap'
};

const logoStyle: CSSProperties = {
  width: 'clamp(60px, 14vw, 100px)',
  opacity: 0.9
};

const tabsContainer: CSSProperties = {
  display: 'flex',
  gap: '0.25rem'
};

const tabStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.45rem 1rem',
  border: 'none',
  borderRadius: '8px',
  background: 'transparent',
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.9rem',
  // Outfit at 600 over a dark bg with low text-alpha (was 0.5) reads soft/blurry.
  // Bump to 700 and lift the alpha so glyph strokes have enough density to crisp up.
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s ease'
};

const activeTabStyle: CSSProperties = {
  background: 'rgba(212, 168, 83, 0.15)',
  color: '#d4a853'
};

const dividerStyle: CSSProperties = {
  width: '1px',
  height: '1.5rem',
  backgroundColor: 'rgba(255, 255, 255, 0.1)'
};

const pillsContainer: CSSProperties = {
  display: 'flex',
  gap: '0.25rem',
  backgroundColor: 'rgba(255, 255, 255, 0.04)',
  borderRadius: '8px',
  padding: '3px'
};

const pillStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.4rem 1rem',
  border: 'none',
  borderRadius: '8px',
  background: 'transparent',
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s ease'
};

const activePillStyle: CSSProperties = {
  background: '#E98C26',
  color: '#ffffff'
};

const togglesContainer: CSSProperties = {
  display: 'flex',
  gap: '0.4rem'
};

const toggleStyle: CSSProperties = {
  padding: '0.25rem 0.65rem',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '6px',
  background: 'transparent',
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.72rem',
  // Was 500 with text-alpha 0.45 — too thin/translucent on small text against dark bg.
  fontWeight: 700,
  letterSpacing: '0.01em',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s ease'
};

const activeToggleStyle: CSSProperties = {
  background: 'rgba(233, 140, 38, 0.15)',
  borderColor: 'rgba(233, 140, 38, 0.4)',
  color: '#E98C26'
};

const filtersBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.35rem 0.75rem',
  border: '1px solid rgba(233, 140, 38, 0.35)',
  borderRadius: '8px',
  background: 'rgba(233, 140, 38, 0.1)',
  color: '#E98C26',
  fontFamily: 'inherit',
  fontSize: '0.8rem',
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  whiteSpace: 'nowrap',
  flexShrink: 0
};

const balanceStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: 'clamp(0.8rem, 2.5vw, 0.95rem)',
  color: 'rgba(255, 255, 255, 0.9)',
  padding: '0.35rem 0.65rem',
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '10px',
  whiteSpace: 'nowrap'
};

const cashBalanceStyle: CSSProperties = {
  fontWeight: 600
};

const promoBalanceStyle: CSSProperties = {
  color: '#E98C26',
  fontSize: '0.75rem',
  fontWeight: 700,
  cursor: 'help',
  padding: '2px 4px',
  borderRadius: '6px',
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: '3px'
};

const profileAvatarStyle: CSSProperties = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid #d4a853',
  boxShadow: '0 0 0 1px rgba(212, 168, 83, 0.3), 0 0 8px rgba(212, 168, 83, 0.15)'
};

const profileBtnStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(255, 255, 255, 0.7)',
  cursor: 'pointer',
  padding: '0.3rem',
  borderRadius: '50%',
  display: 'flex',
  transition: 'all 0.15s ease'
};

const supportBtnStyle: CSSProperties = {
  position: 'relative',
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: 'rgba(255, 255, 255, 0.7)',
  cursor: 'pointer',
  padding: '0.35rem',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s ease',
  fontFamily: 'inherit'
};

/* ── Portrait / mobile filter row ── */
// Goal: replace the wrapping multi-row mess with two clean rows:
//   1) Full-width segmented tab strip + a 40×40 filter icon button on the right.
//   2) A single horizontally-scrollable lane of sub-pills (game modes, tournament
//      types, or Hide Full/Hide Empty toggles, depending on the active tab).
// The scroll lane uses momentum scrolling and edge fade-outs so it visually
// signals "swipe me" without any visible scrollbar — same pattern as the
// existing horizontal carousels in the lobby.

const mobileTabsRow: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: '0.5rem',
  padding: '0.5rem 0.75rem',
  borderTop: '1px solid rgba(212, 168, 83, 0.08)'
};

const mobileSegmentedTabs: CSSProperties = {
  display: 'flex',
  flex: 1,
  minWidth: 0,
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: '10px',
  padding: '3px',
  gap: '2px'
};

const mobileSegmentedTab: CSSProperties = {
  flex: '1 1 0',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.35rem',
  padding: '0.5rem 0.4rem',
  border: 'none',
  borderRadius: '8px',
  background: 'transparent',
  color: 'rgba(255, 255, 255, 0.6)',
  fontFamily: 'inherit',
  fontSize: '0.78rem',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'all 0.18s ease'
};

const mobileSegmentedTabActive: CSSProperties = {
  background: 'linear-gradient(180deg, rgba(212, 168, 83, 0.22) 0%, rgba(212, 168, 83, 0.10) 100%)',
  color: '#f0c97e',
  boxShadow: 'inset 0 0 0 1px rgba(212, 168, 83, 0.35), 0 1px 4px rgba(0, 0, 0, 0.25)'
};

const mobileFiltersIconBtn: CSSProperties = {
  width: 40,
  height: 40,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  border: '1px solid rgba(233, 140, 38, 0.35)',
  background: 'rgba(233, 140, 38, 0.10)',
  color: '#E98C26',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s ease'
};

const mobileSubPillsScroll: CSSProperties = {
  position: 'relative',
  // Edge fade so the lane visually communicates "horizontally scrollable".
  // The mask is purely cosmetic; pointer events still hit the pills underneath.
  WebkitMaskImage:
    'linear-gradient(90deg, transparent 0, #000 14px, #000 calc(100% - 14px), transparent 100%)',
  maskImage:
    'linear-gradient(90deg, transparent 0, #000 14px, #000 calc(100% - 14px), transparent 100%)'
};

const mobileSubPillsTrack: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0 0.75rem 0.6rem',
  overflowX: 'auto',
  overflowY: 'hidden',
  // Hide scrollbar while preserving scroll behavior. Firefox uses scrollbarWidth;
  // WebKit relies on the ::-webkit-scrollbar pseudo (set globally in app.css).
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch'
};

const mobileSubPill: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.45rem 0.85rem',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '999px',
  background: 'rgba(255, 255, 255, 0.04)',
  color: 'rgba(255, 255, 255, 0.75)',
  fontFamily: 'inherit',
  fontSize: '0.78rem',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition: 'all 0.15s ease'
};

const mobileSubPillActive: CSSProperties = {
  background: 'linear-gradient(180deg, #E98C26 0%, #d47a1a 100%)',
  borderColor: 'rgba(255, 255, 255, 0.18)',
  color: '#ffffff',
  boxShadow: '0 2px 8px rgba(233, 140, 38, 0.32)'
};

const mobileSubPillSeparator: CSSProperties = {
  width: 1,
  height: '1.4rem',
  flexShrink: 0,
  background: 'rgba(255, 255, 255, 0.10)',
  margin: '0 0.15rem'
};

const supportBadgeStyle: CSSProperties = {
  position: 'absolute',
  top: '-4px',
  right: '-4px',
  minWidth: '16px',
  height: '16px',
  borderRadius: '8px',
  background: '#C42021',
  color: '#fff',
  fontSize: '0.55rem',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 3px',
  border: '1.5px solid rgba(15, 25, 35, 0.95)'
};
